import {
  Box,
  Button,
  CircularProgress,
  Icon,
  Theme,
  Tooltip,
  Typography,
} from "@outschool/backpack";
import { LearnerQuizWriterTopicSuggestionsQuery } from "@outschool/gql-frontend-generated";
import { faArrowRight, faLockRegular } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import React, { useState } from "react";
import { useAnalytics } from "use-analytics";

import pencilIcon from "./pencil.svg";

interface QuizWriterStartFormProps {
  selectedTopicUid: string | null;
  onSelectTopic: (topicUid: string, topicName: string) => void;
  onCreateQuiz: () => void;
  isQuizGenerating: boolean;
  topics: LearnerQuizWriterTopicSuggestionsQuery["quizWriterTopicSuggestions"];
  topicsLoading: boolean;
}

export function QuizWriterStartForm({
  selectedTopicUid,
  onSelectTopic,
  onCreateQuiz,
  isQuizGenerating,
  topicsLoading,
  topics,
}: QuizWriterStartFormProps) {
  const { t } = useTranslation("learnerApp\\Quiz");
  const { track } = useAnalytics();

  const [isUnlockableTopicTooltipOpen, setIsUnlockableTopicTooltipOpen] =
    useState(false);
  return (
    <Box
      sx={{
        marginBottom: 4,
        backgroundColor: "common.white",
        borderRadius: "20px",
        boxShadow: "0 4px 24px rgba(0, 0, 0, 0.06)",
        border: "1px solid",
        borderColor: "grey.100",
        padding: { xs: 2.5, sm: 3.5 },
      }}
    >
      <Box flex sx={{ flexDirection: "column", gap: 3 }}>
        <Box
          flex
          sx={{
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: 36,
          }}
        >
          <QuizWriterPencilMark />
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "2.25rem",
              textAlign: "center",
              marginBottom: "4px",
            }}
          >
            {t("Quiz Writer")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              fontSize: "1.6rem",
              textAlign: "center",
            }}
          >
            {t("Create a custom 5 question quiz — pick a topic below.")}
          </Typography>
        </Box>
        <Box
          flex
          sx={{
            flexDirection: "column",
            gap: 1.5,
            padding: "0 34px 34px 34px",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              fontSize: "1.6rem",
              marginBottom: "6.5px",
            }}
          >
            {t("What should your quiz be about?")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignItems: { xs: "stretch", sm: "flex-start" },
            }}
          >
            <Box
              flex
              sx={{
                flexDirection: "column",
                gap: 1.5,
                marginTop: 0,
                marginBottom: 20,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "1.5rem",
                  color: "#5c5c5c",
                  marginBottom: "8.5px",
                }}
              >
                {t("Try one of these")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 10,
                  marginTop: 0.5,
                  alignItems: "center",
                  minHeight: topicsLoading ? 40 : undefined,
                }}
              >
                {topicsLoading ? (
                  <CircularProgress
                    size={28}
                    aria-label={t("Loading topics")}
                  />
                ) : (
                  <>
                    {topics.map(topic => (
                      <Button
                        key={topic.uid}
                        type="button"
                        variant="outlined"
                        size="small"
                        disabled={isQuizGenerating}
                        onClick={() => {
                          track("quiz_writer_example_topic_click", {
                            topic: topic.name,
                            topicUid: topic.uid,
                          });
                          onSelectTopic(topic.uid, topic.name);
                        }}
                        sx={{
                          textTransform: "none",
                          fontWeight: 500,
                          borderRadius: "999px",
                          borderColor:
                            selectedTopicUid === topic.uid
                              ? "primary.main"
                              : "grey.300",
                          color: "text.primary",
                          paddingY: 10,
                          paddingX: 20,
                          backgroundColor:
                            selectedTopicUid === topic.uid
                              ? "primary.50"
                              : "transparent",
                          "&:hover:not(:disabled), &:focus:not(:disabled)": {
                            borderColor: "grey.400",
                            backgroundColor: "grey.50",
                            color: "text.primary",
                          },
                          "&:focus-visible": {
                            outlineColor: "grey.500",
                          },
                        }}
                      >
                        {topic.name}
                      </Button>
                    ))}
                    <Tooltip
                      title={t("Complete quizzes to unlock more topics!")}
                      describeChild
                      open={isUnlockableTopicTooltipOpen}
                      onClose={() => {
                        setIsUnlockableTopicTooltipOpen(false);
                      }}
                      onMouseEnter={() => {
                        setIsUnlockableTopicTooltipOpen(true);
                      }}
                    >
                      <Box
                        component="span"
                        tabIndex={0}
                        onClick={() => {
                          setIsUnlockableTopicTooltipOpen(true);
                        }}
                        sx={{
                          display: "inline-flex",
                          verticalAlign: "middle",
                          cursor: "not-allowed",
                          borderRadius: "999px",
                          outline: "none",
                          "&:focus-visible": {
                            boxShadow: (theme: Theme) =>
                              `0 0 0 2px ${theme.palette.grey[500]}`,
                          },
                        }}
                      >
                        <Button
                          type="button"
                          variant="outlined"
                          size="small"
                          disabled
                          aria-label={t(
                            "Complete quizzes to unlock more topics!",
                          )}
                          sx={{
                            textTransform: "none",
                            fontWeight: 500,
                            borderRadius: "999px",
                            borderColor: "grey.200",
                            color: "text.disabled",
                            paddingY: 10,
                            paddingX: 20,
                            minWidth: 72,
                            minHeight: 36,
                            backgroundColor: "grey.100",
                            pointerEvents: "none",
                            "&.Mui-disabled": {
                              borderColor: "grey.200",
                              color: "text.disabled",
                              backgroundColor: "grey.100",
                            },
                          }}
                        >
                          <Icon
                            icon={faLockRegular}
                            sx={{
                              display: "block",
                              margin: "0 auto",
                              fontSize: "1.4rem",
                              marginRight: "8px",
                            }}
                          />
                          {t("Take a quiz to unlock")}
                        </Button>
                      </Box>
                    </Tooltip>
                  </>
                )}
              </Box>
            </Box>
            <Button
              onClick={onCreateQuiz}
              disabled={isQuizGenerating || !selectedTopicUid}
              variant="contained"
              sx={{ fontWeight: 800, fontSize: "1.6rem", padding: "8px 21px" }}
            >
              {isQuizGenerating ? t("Creating...") : t("Create quiz")}{" "}
              <Icon icon={faArrowRight} sx={{ marginLeft: "6px" }} />
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function QuizWriterPencilMark() {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        marginBottom: "17px",
      }}
    >
      <Box
        sx={{
          height: 68,
          width: 68,
          borderRadius: "18px",
          backgroundColor: "#ffdbf0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2.5,
        }}
      >
        <Box
          component="img"
          src={pencilIcon}
          alt=""
          sx={{ width: 36, height: 36, display: "block" }}
        />
      </Box>
    </Box>
  );
}
